import { presenceName } from "@nexxt/ui";
import type { FollowedObject } from "../follow/followed-object";
import { m } from "../paraglide/messages.js";
import type { BellFollowFacts, BellItem } from "./bell-item";

// What an item of the bell says (ADR-308): a row of a followed object in the app's words, its status as the badges of
// the app say it (`statusOf` of the `FollowClient`); any other item, and a row written before its facts, by its title.

/** The status of an object in the words of the app's badges. */
type StatusOf = (object: Pick<FollowedObject, "entityType" | "status">) => string;

/** What the user calls an object: a noun where the number does not say it. */
function objectName(entityType: string | null, reference: string): string {
  switch (entityType) {
    case "procurement.inbound":
      return m.bell_object_inbound({ reference });
    case "catalog.linesheet":
      return m.bell_object_linesheet({ reference });
    // ADR-309: a task is called by its linesheet
    case "catalog.attribute_task":
      return m.bell_object_attribute_task({ reference });
    case "claims.claim":
      return m.bell_object_claim({ reference });
    default:
      return reference;
  }
}

/** What the changes did: the proposals and the prices are named when they are all they did. */
function activityWords({ kinds, lines }: Extract<BellFollowFacts, { kind: "activity" }>): string {
  const only = kinds.length === 1 ? kinds[0] : null;
  if (only === "proposal") {
    return lines === 1 ? m.bell_activity_proposals_one() : m.bell_activity_proposals_many({ count: lines });
  }
  if (only === "prices") {
    if (lines === 0) return m.bell_activity_prices();
    return lines === 1 ? m.bell_activity_prices_one() : m.bell_activity_prices_many({ count: lines });
  }
  if (lines === 0) return m.bell_activity_changed();
  return lines === 1 ? m.bell_activity_lines_one() : m.bell_activity_lines_many({ count: lines });
}

/** The words of an item: "PO-000123 is now In validation", "Linesheet SS27: 3 lines changed (2 saves)". */
export function bellTitle(item: BellItem, statusOf: StatusOf): string {
  const { facts, entityType } = item;
  if (!facts) return item.title;
  const object = objectName(entityType, facts.reference);
  if (facts.kind === "status") {
    return m.bell_follow_status({ object, status: statusOf({ entityType: entityType ?? "", status: facts.to }) });
  }
  const saves = facts.saves === 1 ? m.bell_activity_saves_one() : m.bell_activity_saves_many({ count: facts.saves });
  return m.bell_follow_activity({ object, what: activityWords(facts), saves });
}

/** Who changed it, "by Tomorrow", or nothing for an item that does not say. */
export function bellBy(item: BellItem): string | null {
  const by = item.facts?.by ?? [];
  return by.length === 0 ? null : m.bell_by({ names: by.map((who) => presenceName({ key: "", ...who })).join(", ") });
}
